/**
 * StudentDetailsStep - Step 2: Student information form (supports multiple siblings)
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { User, ArrowRight, ArrowLeft, Users, Percent } from 'lucide-react';

export const StudentDetailsStep = ({ 
  studentForm, 
  setStudentForm, 
  studentForms = [],
  setStudentForms,
  allStudents = [],
  hasSiblings = false,
  onNext, 
  onBack 
}) => {
  // Update a specific student form field
  const updateStudentForm = (index, field, value) => {
    if (setStudentForms) {
      const updated = [...studentForms];
      updated[index] = { ...updated[index], [field]: value };
      setStudentForms(updated);
    }
    // Also update legacy single form if it's the first student
    if (index === 0 && setStudentForm) {
      setStudentForm({ ...studentForm, [field]: value });
    }
  };

  const handleNext = () => {
    // Validate all student forms if siblings, otherwise just the single form
    if (hasSiblings && studentForms.length > 0) {
      for (let i = 0; i < studentForms.length; i++) {
        if (!studentForms[i].name) {
          toast.error(`Please enter name for ${studentForms[i].name || `Student ${i + 1}`}`);
          return;
        }
      }
    } else if (!studentForm.name) {
      toast.error('Please enter student name');
      return;
    }
    onNext();
  };

  // Multi-student UI
  if (hasSiblings && studentForms.length > 1) {
    return (
      <div className="space-y-6">
        <Card className="bg-purple-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6 text-purple-400" />
              <div>
                <p className="text-purple-400 font-semibold">
                  Confirm Details for {studentForms.length} Students
                </p>
                <p className="text-purple-400/70 text-sm">
                  Please verify the information for each student below.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {studentForms.map((form, index) => (
          <Card key={form.id || index} className="bg-white/5 border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-[#FF007F]" />
                {form.name || `Student ${index + 1}`}
                {form.discount_percent > 0 && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 ml-2">
                    <Percent className="w-3 h-3 mr-1" />
                    {form.discount_percent}% off
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-white/70">Student's Full Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => updateStudentForm(index, 'name', e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Enter full name"
                  data-testid={`student-name-${index}`}
                />
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">Date of Birth</Label>
                  <Input
                    type="date"
                    value={form.birth_date}
                    onChange={(e) => updateStudentForm(index, 'birth_date', e.target.value)}
                    className="bg-white/5 border-white/10 text-white"
                    max={new Date().toISOString().split('T')[0]}
                    min={`${new Date().getFullYear() - 75}-01-01`}
                    data-testid={`student-dob-${index}`}
                  />
                </div>
                <div>
                  <Label className="text-white/70">Gender</Label>
                  <select
                    value={form.gender}
                    onChange={(e) => updateStudentForm(index, 'gender', e.target.value)}
                    className="w-full h-10 bg-white/5 border border-white/10 text-white rounded-md px-3"
                    data-testid={`student-gender-${index}`}
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label className="text-white/70">Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) => updateStudentForm(index, 'address', e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  placeholder="Full address"
                  data-testid={`student-address-${index}`}
                />
              </div>
            </CardContent>
          </Card>
        ))}

        <div className="flex gap-3 pt-4">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="flex-1 border-white/10 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button 
            onClick={handleNext}
            className="flex-1 bg-[#FF007F] hover:bg-[#D6006A] text-white"
            data-testid="step2-continue"
          >
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Single student UI (original)
  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <User className="w-5 h-5 text-[#FF007F]" />
          Confirm Student Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-white/70">Student's Full Name *</Label>
          <Input
            value={studentForm.name}
            onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
            className="bg-white/5 border-white/10 text-white"
            placeholder="Enter full name"
            data-testid="student-name"
          />
        </div>
        
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-white/70">Date of Birth</Label>
            <Input
              type="date"
              value={studentForm.birth_date}
              onChange={(e) => setStudentForm({ ...studentForm, birth_date: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              max={new Date().toISOString().split('T')[0]}
              min={`${new Date().getFullYear() - 75}-01-01`}
              data-testid="student-dob"
            />
          </div>
          <div>
            <Label className="text-white/70">Gender</Label>
            <select
              value={studentForm.gender}
              onChange={(e) => setStudentForm({ ...studentForm, gender: e.target.value })}
              className="w-full h-10 bg-white/5 border border-white/10 text-white rounded-md px-3"
              data-testid="student-gender"
            >
              <option value="">Select...</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
        
        <div>
          <Label className="text-white/70">Address</Label>
          <Input
            value={studentForm.address}
            onChange={(e) => setStudentForm({ ...studentForm, address: e.target.value })}
            className="bg-white/5 border-white/10 text-white"
            placeholder="Full address"
            data-testid="student-address"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="flex-1 border-white/10 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button 
            onClick={handleNext}
            className="flex-1 bg-[#FF007F] hover:bg-[#D6006A] text-white"
            data-testid="step2-continue"
          >
            Continue <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default StudentDetailsStep;
